import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { Calendar, Clock, MapPin, Volume2, VolumeX, Sparkles, Navigation, Heart, Send, Share2, CheckCircle2, ChevronDown, Film } from 'lucide-react';
import type { TemplateMetadata, InvitationData } from '../types';
import { registry } from '../registry';
import './theme.css';

const META: TemplateMetadata = {
  id: 'video-02-kirtan-divine',
  name: 'Kirtan & Radha Krishna Divine Love Video',
  occasionType: 'WEDDING',
  description: 'A cinematic scroll-driven Radha Krishna devotional video invitation template with dynamic ceremony video switching, peacock artwork overlays, and divine typography.',
  isPremium: true,
  tags: ['kirtan', 'radha-krishna', 'devotional', 'wedding', 'marathi', 'hindi', 'peacock', 'god', 'divine-love', 'cinematic', 'video', 'scroll-experience', 'animated'],
  colorScheme: { primary: '#D4AF37', accent: '#7B1E26', bg: '#0D0518' },
};

const CEREMONIES = [
  {
    id: 'kirtan',
    name: 'Bhajan & Divine Kirtan',
    shortName: 'Kirtan',
    hindiName: 'दिव्य भक्ती संकीर्तन',
    videoBg: '/assets/templates/video-02-kirtan-divine/kirtan_bg.mp4',
    defaultDate: 'Tuesday, 28th October 2025',
    defaultTime: '05:00 PM Onwards',
    description: 'Immerse in joyful Radha Krishna bhajan & divine sangeet kirtan with family & friends.',
  },
  {
    id: 'poojan',
    name: 'Devkarya & Ganesh Poojan',
    shortName: 'Poojan',
    hindiName: 'देवकार्य व श्रीगणेश पूजन',
    videoBg: '/assets/templates/video-02-kirtan-divine/poojan_bg.mp4',
    defaultDate: 'Wednesday, 29th October 2025',
    defaultTime: '09:00 AM (Shubh Muhurat)',
    description: 'Seeking divine blessings of Lord Ganesha and Kuldevta for a prosperous wedding.',
  },
  {
    id: 'haldi',
    name: 'Haldi Rasam & Ubtan',
    shortName: 'Haldi',
    hindiName: 'हळदीचा शुभ सोहळा',
    videoBg: '/assets/templates/video-02-kirtan-divine/haldi_bg.mp4',
    defaultDate: 'Thursday, 30th October 2025',
    defaultTime: '10:00 AM Onwards',
    description: 'A joyful morning of golden turmeric, laughter, song, and traditional blessings.',
  },
  {
    id: 'mehendi',
    name: 'Mehendi Ki Raat',
    shortName: 'Mehendi',
    hindiName: 'रंगोभरी मेहंदी रात्र',
    videoBg: '/assets/templates/video-02-kirtan-divine/mehendi_bg.mp4',
    defaultDate: 'Thursday, 30th October 2025',
    defaultTime: '04:00 PM Onwards',
    description: 'Intricate henna designs, folk music, and vibrant celebrations with loved ones.',
  },
  {
    id: 'sangeet',
    name: 'Sangeet & Nritya Sandhya',
    shortName: 'Sangeet',
    hindiName: 'संगीत व नृत्य संध्या',
    videoBg: '/assets/templates/video-02-kirtan-divine/sangeet_bg.mp4',
    defaultDate: 'Thursday, 30th October 2025',
    defaultTime: '07:30 PM Onwards',
    description: 'A grand musical evening filled with dance performances, music, and celebration.',
  },
  {
    id: 'wedding',
    name: 'Shubh Vivah (Wedding)',
    shortName: 'Vivah',
    hindiName: 'शुभ विवाह सोहळा',
    videoBg: '/assets/templates/video-02-kirtan-divine/wedding_bg.mp4',
    defaultDate: 'Friday, 31st October 2025',
    defaultTime: '12:35 PM (Shubh Muhurat)',
    description: 'Sacred seven vows around the holy fire under divine Radha Krishna blessings.',
  },
  {
    id: 'reception',
    name: 'Grand Reception',
    shortName: 'Reception',
    hindiName: 'स्नेहभोजन व स्वागत',
    videoBg: '/assets/templates/video-02-kirtan-divine/reception_bg.mp4',
    defaultDate: 'Friday, 31st October 2025',
    defaultTime: '07:30 PM Onwards',
    description: 'A grand evening banquet celebrating the newlyweds with family and friends.',
  },
];

export function VideoKirtanDivineTemplate({ data }: { data: InvitationData }) {
  const customData = (data.customData as any) || {};
  const weddingData = customData.wedding || {};
  const expMeta = customData.experience?.meta || {};
  
  const groomName = weddingData.groom?.fullName || weddingData.groom?.nickname || expMeta.groomName || expMeta.secondaryName || (data.eventName ? data.eventName.split(/ (?:&|and) /i)[0] : '') || 'Raghav';
  const brideName = weddingData.bride?.fullName || weddingData.bride?.nickname || expMeta.brideName || expMeta.primaryName || (data.eventName ? data.eventName.split(/ (?:&|and) /i)[1] : '') || 'Riya';
  const hostNames = data.hostName || customData.hostNames || expMeta.hostName || 'Mrs. Mansi Chopra & Mr. Lalit Chopra';
  const hostFamily = data.hostName || customData.hostFamily || 'Chopra Pariwar';
  
  const eventDate = ((data as any)._dateInput ? (data as any)._dateInput : (data.eventDate ? new Date(data.eventDate).toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' }) : '')) || expMeta.weddingDate || expMeta.eventDate || 'Tuesday, 28th October 2025';
  const eventTime = customData.eventTime || '06:00 PM Onwards';
  const venueName = data.eventVenue || customData.venueAddress || 'Hallmark Banquets';
  const venueAddress = data.eventVenue || customData.venueAddress || 'Anand Vihar, Delhi - 110092';

  const bridePhoto = expMeta.referenceImages?.bride || expMeta.bridePhotoUrl || customData.bridePhotoUrl;
  const groomPhoto = expMeta.referenceImages?.groom || expMeta.groomPhotoUrl || customData.groomPhotoUrl;
  const couplePhoto = expMeta.referenceImages?.couple || expMeta.couplePhotoUrl || expMeta.referenceImages?.primary || customData.couplePhotoUrl;
  const customVideoUrl = customData.customVideoUrl || expMeta.customVideoUrl || customData.preWeddingVideoUrl;

  const [activeVideoBg, setActiveVideoBg] = useState<string>(CEREMONIES[0].videoBg);
  const [activeSectionId, setActiveSectionId] = useState<string>('section-hero');
  const [isPlayingAudio, setIsPlayingAudio] = useState<boolean>(false);
  const [showRsvpModal, setShowRsvpModal] = useState<boolean>(false);
  const [showShareToast, setShowShareToast] = useState<boolean>(false);
  const audioRef = useRef<HTMLAudioElement | null>(null);

  // Scroll position observer to update background video dynamically
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const sectionId = entry.target.id;
            setActiveSectionId(sectionId);
            const foundCeremony = CEREMONIES.find((c) => `section-${c.id}` === sectionId);
            if (foundCeremony) {
              setActiveVideoBg(foundCeremony.videoBg);
            } else if (sectionId === 'section-hero') {
              setActiveVideoBg(CEREMONIES[0].videoBg);
            } else if (sectionId === 'section-rsvp') {
              setActiveVideoBg(CEREMONIES[CEREMONIES.length - 1].videoBg);
            }
          }
        });
      },
      { threshold: 0.15, rootMargin: '-10% 0px -10% 0px' }
    );

    const sections = document.querySelectorAll('.scroll-ceremony-section');
    sections.forEach((sec) => observer.observe(sec));

    return () => {
      sections.forEach((sec) => observer.unobserve(sec));
    };
  }, []);

  useEffect(() => {
    audioRef.current = new Audio('/assets/templates/video-02-kirtan-divine/audio.mp3');
    audioRef.current.loop = true;

    return () => {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current = null;
      }
    };
  }, []);

  const toggleAudio = () => {
    if (!audioRef.current) return;
    if (isPlayingAudio) {
      audioRef.current.pause();
      setIsPlayingAudio(false);
    } else {
      audioRef.current.play().then(() => {
        setIsPlayingAudio(true);
      }).catch(err => {
        console.warn('Audio play prevented:', err);
      });
    }
  };

  const scrollToSection = (id: string) => {
    const el = document.getElementById(id);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  };

  const handleShare = () => {
    if (navigator.share) {
      navigator.share({
        title: `Wedding Invitation: ${groomName} & ${brideName}`,
        text: `You are cordially invited to the Divine Kirtan & Wedding of ${groomName} & ${brideName}.`,
        url: window.location.href,
      }).catch(() => {});
    } else {
      navigator.clipboard.writeText(window.location.href);
      setShowShareToast(true);
      setTimeout(() => setShowShareToast(false), 3000);
    }
  };

  return (
    <div className="relative min-h-screen w-full bg-[#0D0518] text-amber-100 overflow-x-hidden font-neuton selection:bg-amber-500 selection:text-black">
      
      {/* Full-Screen Instant-Crossfading Preloaded Video Loops */}
      <div className="fixed inset-0 z-0 overflow-hidden pointer-events-none bg-black">
        {CEREMONIES.map((ceremony) => {
          const isActive = activeVideoBg === ceremony.videoBg;
          return (
            <video
              key={ceremony.id}
              autoPlay
              loop
              muted
              playsInline
              className={`absolute inset-0 w-full h-full object-cover object-center scale-100 filter brightness-110 contrast-105 saturate-110 transition-opacity duration-700 ${
                isActive ? 'opacity-100' : 'opacity-0 pointer-events-none'
              }`}
              src={ceremony.videoBg}
            />
          );
        })}
        {/* Crystal-Clear 100% Crisp Video Pass-Through */}
        <div className="absolute inset-0 bg-black/15 pointer-events-none" />
      </div>

      {/* Floating Audio Control Button */}
      <button
        onClick={toggleAudio}
        aria-label="Toggle Background Music"
        className="fixed top-3 right-3 z-50 p-2.5 rounded-full bg-amber-950/85 border border-amber-400/50 text-amber-300 shadow-[0_0_20px_rgba(212,175,55,0.4)] backdrop-blur-md hover:scale-110 active:scale-95 transition-all cursor-pointer"
      >
        {isPlayingAudio ? (
          <Volume2 className="w-4 h-4 animate-pulse text-amber-300" />
        ) : (
          <VolumeX className="w-4 h-4 text-amber-400/60" />
        )}
      </button>

      {/* Sticky Quick Jump Navigation Bar */}
      <div className="sticky top-2 z-40 max-w-xl mx-auto px-3 my-2">
        <div className="flex items-center gap-1.5 p-1.5 rounded-2xl bg-black/75 border border-amber-500/40 backdrop-blur-md overflow-x-auto scrollbar-none shadow-[0_4px_20px_rgba(0,0,0,0.6)]">
          <button
            onClick={() => scrollToSection('section-hero')}
            className={`px-3 py-1 rounded-xl text-[11px] font-lemonmilk whitespace-nowrap transition-all cursor-pointer ${
              activeSectionId === 'section-hero'
                ? 'bg-gradient-to-r from-amber-500 to-amber-600 text-amber-950 font-bold shadow-md'
                : 'text-amber-200/70 hover:text-amber-200 hover:bg-amber-500/10'
            }`}
          >
            Home
          </button>
          {CEREMONIES.map((ceremony) => (
            <button
              key={ceremony.id}
              onClick={() => scrollToSection(`section-${ceremony.id}`)}
              className={`px-3 py-1 rounded-xl text-[11px] font-lemonmilk whitespace-nowrap transition-all cursor-pointer ${
                activeSectionId === `section-${ceremony.id}`
                  ? 'bg-gradient-to-r from-amber-500 to-amber-600 text-amber-950 font-bold shadow-md'
                  : 'text-amber-200/70 hover:text-amber-200 hover:bg-amber-500/10'
              }`}
            >
              {ceremony.shortName}
            </button>
          ))}
          <button
            onClick={() => scrollToSection('section-rsvp')}
            className={`px-3 py-1 rounded-xl text-[11px] font-lemonmilk whitespace-nowrap transition-all cursor-pointer ${
              activeSectionId === 'section-rsvp'
                ? 'bg-gradient-to-r from-amber-500 to-amber-600 text-amber-950 font-bold shadow-md'
                : 'text-amber-200/70 hover:text-amber-200 hover:bg-amber-500/10'
            }`}
          >
            RSVP
          </button>
        </div>
      </div>

      {/* Toast Notification */}
      <AnimatePresence>
        {showShareToast && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            className="fixed top-4 left-1/2 -translate-x-1/2 z-50 px-4 py-2 rounded-xl bg-amber-500 text-amber-950 font-bold text-xs shadow-lg flex items-center gap-2"
          >
            <CheckCircle2 className="w-4 h-4" /> Link copied to clipboard!
          </motion.div>
        )}
      </AnimatePresence>

      {/* Scroll-Driven Ceremony Cards Container */}
      <main className="relative z-10 w-full max-w-xl mx-auto px-3 py-4 space-y-12 sm:space-y-16">
        
        {/* SECTION 0: HERO & WELCOME CARD */}
        <section id="section-hero" className="scroll-ceremony-section min-h-[85vh] flex flex-col items-center justify-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="glass-divine-card w-full rounded-3xl p-6 sm:p-8 text-center relative overflow-hidden border border-amber-500/40 shadow-[0_10px_50px_rgba(0,0,0,0.85)]"
          >
            {/* Peacock Artwork Accent */}
            <div className="absolute -top-4 -right-4 w-28 sm:w-36 pointer-events-none z-20 opacity-90 animate-pulse-glow">
              <img
                src="/assets/templates/video-02-kirtan-divine/peacock_artwork.png"
                alt="Peacock Artwork"
                className="w-full h-auto drop-shadow-[0_0_15px_rgba(212,175,55,0.5)]"
              />
            </div>

            {/* Top Header - Shree Ganesha Header Artwork */}
            <div className="relative inline-block mx-auto max-w-[220px] sm:max-w-[280px] mb-3">
              <img
                src="/assets/templates/video-02-kirtan-divine/ganesh_header.png"
                alt="Shree Ganeshay Namah"
                className="w-full h-auto drop-shadow-[0_0_12px_rgba(212,175,55,0.6)] filter brightness-110 animate-divine-float"
              />
            </div>

            <p className="text-xs sm:text-sm text-amber-200/90 tracking-widest font-neuton uppercase mb-1">
              || {hostNames} ||
            </p>
            <p className="text-xs sm:text-sm text-amber-300/80 font-neuton italic mb-4">
              Cordially invite you to the Celebration of Divine Love
            </p>

            {/* Couple Names Header */}
            <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-amber-500/20 border border-amber-400/30 text-amber-300 text-xs font-lemonmilk mb-2">
              <Sparkles className="w-3.5 h-3.5" /> Shubh Vivah &amp; Kirtan
            </div>

            <h1 className="text-4xl sm:text-6xl font-brotherland gold-gradient-text leading-tight py-2">
              {groomName} <span className="text-2xl sm:text-3xl text-amber-300">&amp;</span> {brideName}
            </h1>

            <p className="text-xs sm:text-sm text-amber-200/80 font-neuton italic mt-2 mb-4">
              "Under the divine grace of Radha Krishna, two souls unite in eternal harmony."
            </p>

            <div className="flex flex-col sm:flex-row items-center justify-center gap-3 pt-3 border-t border-amber-500/20 text-xs text-amber-300">
              <div className="flex items-center gap-1.5">
                <Calendar className="w-4 h-4 text-amber-400 shrink-0" />
                <span>{eventDate}</span>
              </div>
              <span className="hidden sm:inline text-amber-500/60">•</span>
              <div className="flex items-center gap-1.5">
                <MapPin className="w-4 h-4 text-amber-400 shrink-0" />
                <span className="truncate">{venueName}</span>
              </div>
            </div>

            {/* Uploaded Bride & Groom Showcase Section */}
            {(bridePhoto || groomPhoto || couplePhoto) && (
              <div className="my-6 grid grid-cols-2 gap-4">
                {groomPhoto && (
                  <div className="flex flex-col items-center">
                    <div className="w-24 h-24 sm:w-28 sm:h-28 rounded-full p-1 bg-gradient-to-tr from-amber-400 via-yellow-300 to-amber-600 shadow-[0_0_25px_rgba(212,175,55,0.6)] overflow-hidden">
                      <img src={groomPhoto} alt={groomName} className="w-full h-full object-cover rounded-full" />
                    </div>
                    <span className="mt-2 text-xs font-lemonmilk text-amber-200 tracking-wider">{groomName}</span>
                    <span className="text-[10px] text-amber-300/70 uppercase">Groom</span>
                  </div>
                )}
                {bridePhoto && (
                  <div className="flex flex-col items-center">
                    <div className="w-24 h-24 sm:w-28 sm:h-28 rounded-full p-1 bg-gradient-to-tr from-amber-400 via-yellow-300 to-amber-600 shadow-[0_0_25px_rgba(212,175,55,0.6)] overflow-hidden">
                      <img src={bridePhoto} alt={brideName} className="w-full h-full object-cover rounded-full" />
                    </div>
                    <span className="mt-2 text-xs font-lemonmilk text-amber-200 tracking-wider">{brideName}</span>
                    <span className="text-[10px] text-amber-300/70 uppercase">Bride</span>
                  </div>
                )}
                {couplePhoto && !bridePhoto && !groomPhoto && (
                  <div className="col-span-2 flex flex-col items-center my-2">
                    <div className="w-full max-w-sm h-48 sm:h-56 rounded-2xl p-1 bg-gradient-to-tr from-amber-400 via-yellow-300 to-amber-600 shadow-[0_0_30px_rgba(212,175,55,0.5)] overflow-hidden">
                      <img src={couplePhoto} alt={`${groomName} & ${brideName}`} className="w-full h-full object-cover rounded-xl" />
                    </div>
                    <span className="mt-2 text-xs font-lemonmilk text-amber-200 tracking-wider">{groomName} &amp; {brideName}</span>
                  </div>
                )}
              </div>
            )}

            <div className="mt-6 flex items-center justify-center gap-2">
              <button
                onClick={() => setShowRsvpModal(true)}
                className="py-2.5 px-6 rounded-xl bg-gradient-to-r from-amber-400 via-yellow-500 to-amber-600 text-amber-950 font-lemonmilk font-bold text-xs shadow-[0_0_20px_rgba(212,175,55,0.4)] hover:scale-105 active:scale-95 transition-all cursor-pointer"
              >
                Confirm RSVP
              </button>
            </div>

            {/* Scroll Indicator */}
            <div className="mt-8 flex flex-col items-center text-amber-400/80 text-[11px] font-lemonmilk animate-bounce">
              <span>Scroll down for Ceremonies</span>
              <ChevronDown className="w-4 h-4 mt-0.5" />
            </div>
          </motion.div>
        </section>

        {/* SECTIONS 1 TO 7: CEREMONY SECTIONS */}
        {CEREMONIES.map((ceremony, idx) => (
          <section
            key={ceremony.id}
            id={`section-${ceremony.id}`}
            className="scroll-ceremony-section py-4 min-h-[70vh] flex flex-col items-center justify-center"
          >
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: false, amount: 0.3 }}
              transition={{ duration: 0.6 }}
              className="glass-divine-card w-full rounded-3xl p-6 sm:p-8 text-center relative overflow-hidden border border-amber-500/40 shadow-[0_10px_45px_rgba(0,0,0,0.8)]"
            >
              {/* Peacock Artwork Accent on odd sections */}
              {idx % 2 === 1 && (
                <div className="absolute -top-4 -left-4 w-24 sm:w-32 pointer-events-none z-20 opacity-80 scale-x-[-1]">
                  <img
                    src="/assets/templates/video-02-kirtan-divine/peacock_artwork.png"
                    alt="Peacock Artwork"
                    className="w-full h-auto drop-shadow-[0_0_15px_rgba(212,175,55,0.4)]"
                  />
                </div>
              )}

              <span className="text-xs sm:text-sm font-lemonmilk text-amber-400/90 tracking-widest uppercase block mb-1">
                {ceremony.hindiName}
              </span>

              <h2 className="text-2xl sm:text-4xl font-brotherland text-amber-200 gold-gradient-text drop-shadow-[0_2px_10px_rgba(212,175,55,0.4)] py-1">
                {ceremony.name}
              </h2>

              <div className="w-16 h-0.5 bg-gradient-to-r from-transparent via-amber-400 to-transparent mx-auto my-3" />

              <p className="text-xs sm:text-sm text-amber-100/90 italic leading-relaxed max-w-md mx-auto mb-5">
                {ceremony.description}
              </p>

              {/* Timing & Date Details */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 p-4 rounded-2xl bg-black/50 border border-amber-500/30 text-xs text-left mb-4">
                <div className="flex items-center gap-2 text-amber-200">
                  <Calendar className="w-4 h-4 text-amber-400 shrink-0" />
                  <span>{eventDate || ceremony.defaultDate}</span>
                </div>
                <div className="flex items-center gap-2 text-amber-200">
                  <Clock className="w-4 h-4 text-amber-400 shrink-0" />
                  <span>{eventTime || ceremony.defaultTime}</span>
                </div>
              </div>

              {/* Venue Info */}
              <div className="p-3 rounded-2xl bg-amber-500/10 border border-amber-500/30 flex items-center justify-between gap-3 text-xs text-left">
                <div className="space-y-0.5 min-w-0">
                  <div className="flex items-center gap-1.5 text-amber-300 font-semibold truncate">
                    <MapPin className="w-3.5 h-3.5 text-amber-400 shrink-0" />
                    <span className="truncate">{venueName}</span>
                  </div>
                  <p className="text-[11px] text-amber-200/80 pl-5 truncate">{venueAddress}</p>
                </div>
                <a
                  href={`https://maps.google.com/?q=${encodeURIComponent(`${venueName} ${venueAddress}`)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="px-3 py-1.5 rounded-xl bg-amber-500/20 hover:bg-amber-500/30 border border-amber-400/40 text-amber-300 flex items-center gap-1 text-[11px] font-lemonmilk transition-colors shrink-0"
                >
                  <Navigation className="w-3 h-3" /> Directions
                </a>
              </div>
            </motion.div>
          </section>
        ))}

        {/* SECTION: CUSTOM UPLOADED PRE-WEDDING VIDEO */}
        {customVideoUrl && (
          <section id="section-prewedding-video" className="scroll-ceremony-section py-4 min-h-[50vh] flex flex-col items-center justify-center">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: false, amount: 0.3 }}
              transition={{ duration: 0.6 }}
              className="glass-divine-card w-full rounded-3xl p-6 sm:p-8 text-center relative overflow-hidden border border-amber-500/40 shadow-[0_10px_50px_rgba(0,0,0,0.85)] space-y-4"
            >
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-amber-500/20 border border-amber-400/40 text-amber-300 text-xs font-lemonmilk tracking-widest uppercase">
                <Film className="w-3.5 h-3.5" /> Pre-Wedding Story
              </div>
              <h2 className="font-brotherland text-3xl sm:text-4xl text-amber-300">Our Special Memories</h2>
              <div className="relative rounded-2xl overflow-hidden border border-amber-500/40 shadow-2xl bg-black/90">
                <video
                  src={customVideoUrl}
                  controls
                  playsInline
                  className="w-full max-h-[420px] object-contain rounded-2xl"
                />
              </div>
            </motion.div>
          </section>
        )}

        {/* SECTION 8: VENUE, MAP & RSVP FOOTER SECTION */}
        <section id="section-rsvp" className="scroll-ceremony-section py-4 min-h-[75vh] flex flex-col items-center justify-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: false, amount: 0.3 }}
            transition={{ duration: 0.6 }}
            className="glass-divine-card w-full rounded-3xl p-6 sm:p-8 text-center relative overflow-hidden border border-amber-500/40 shadow-[0_10px_50px_rgba(0,0,0,0.85)] space-y-6"
          >
            <div>
              <Heart className="w-8 h-8 text-amber-400 mx-auto mb-2 animate-pulse" />
              <h2 className="text-2xl sm:text-3xl font-lemonmilk text-amber-200">Venue &amp; RSVP</h2>
              <p className="text-xs sm:text-sm text-amber-200/80 mt-1">
                We await your presence to bless the newlyweds {groomName} &amp; {brideName}.
              </p>
            </div>

            {/* Venue Location Details */}
            <div className="p-4 rounded-2xl bg-black/60 border border-amber-500/40 text-left space-y-2">
              <div className="flex items-center gap-2 text-amber-300 font-bold text-sm">
                <MapPin className="w-4 h-4 text-amber-400" />
                <span>{venueName}</span>
              </div>
              <p className="text-xs text-amber-200/80 pl-6">{venueAddress}</p>
              <p className="text-xs text-amber-300/90 pl-6 font-semibold">Hosted By: {hostFamily}</p>
              <a
                href={`https://maps.google.com/?q=${encodeURIComponent(`${venueName} ${venueAddress}`)}`}
                target="_blank"
                rel="noopener noreferrer"
                className="mt-2 inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-amber-500/20 hover:bg-amber-500/30 border border-amber-400/40 text-amber-300 font-lemonmilk text-xs transition-colors"
              >
                <Navigation className="w-3.5 h-3.5" /> Open Google Maps Directions
              </a>
            </div>

            {/* Action Buttons */}
            <div className="flex flex-col sm:flex-row items-center gap-3 pt-2">
              <button
                onClick={() => setShowRsvpModal(true)}
                className="w-full py-3 px-6 rounded-xl bg-gradient-to-r from-amber-400 via-yellow-500 to-amber-600 text-amber-950 font-lemonmilk font-bold text-xs shadow-[0_0_25px_rgba(212,175,55,0.5)] hover:scale-[1.02] active:scale-95 transition-all flex items-center justify-center gap-2 cursor-pointer"
              >
                <Send className="w-4 h-4" /> Confirm Your RSVP
              </button>

              <button
                onClick={handleShare}
                className="w-full py-3 px-6 rounded-xl bg-amber-950/90 border border-amber-400/50 text-amber-300 font-lemonmilk text-xs hover:bg-amber-900/90 transition-all flex items-center justify-center gap-2 cursor-pointer"
              >
                <Share2 className="w-4 h-4" /> Share Invitation
              </button>
            </div>
          </motion.div>
        </section>

      </main>

      {/* RSVP Modal */}
      <AnimatePresence>
        {showRsvpModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-md">
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.9 }}
              className="glass-divine-card max-w-md w-full p-6 rounded-3xl relative text-center border border-amber-500/50"
            >
              <button
                onClick={() => setShowRsvpModal(false)}
                className="absolute top-4 right-4 text-amber-400 hover:text-amber-200 text-lg cursor-pointer"
              >
                ✕
              </button>
              <Heart className="w-7 h-7 text-amber-400 mx-auto mb-1" />
              <h3 className="font-lemonmilk text-lg text-amber-200 mb-1">RSVP Confirmation</h3>
              <p className="text-xs text-amber-200/80 mb-4">
                Please let us know if you will be joining the divine celebration for {groomName} &amp; {brideName}.
              </p>
              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  setShowRsvpModal(false);
                  alert('Thank you! Your RSVP response has been recorded.');
                }}
                className="space-y-3 text-left"
              >
                <div>
                  <label className="text-xs text-amber-300 font-semibold">Your Full Name</label>
                  <input
                    required
                    type="text"
                    placeholder="e.g. Rajesh Sharma"
                    className="w-full mt-1 px-3 py-2 rounded-xl bg-black/60 border border-amber-500/30 text-amber-100 text-sm focus:outline-none focus:border-amber-400"
                  />
                </div>
                <div>
                  <label className="text-xs text-amber-300 font-semibold">Attending Guests Count</label>
                  <select className="w-full mt-1 px-3 py-2 rounded-xl bg-black/60 border border-amber-500/30 text-amber-100 text-sm focus:outline-none focus:border-amber-400">
                    <option value="1">1 Person</option>
                    <option value="2">2 Persons</option>
                    <option value="3">Family (3+ Persons)</option>
                  </select>
                </div>
                <button
                  type="submit"
                  className="w-full py-2.5 rounded-xl bg-gradient-to-r from-amber-400 to-amber-600 text-amber-950 font-bold text-sm shadow-md hover:scale-[1.02] transition-transform mt-1 cursor-pointer"
                >
                  Submit RSVP
                </button>
              </form>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
}

// Self-register in template registry
registry.register({
  metadata: META,
  component: VideoKirtanDivineTemplate,
});
